import {
  type VariantColorsResolver,
  defaultVariantColorsResolver,
  alpha,
  type VariantColorResolverResult,
} from "@mantine/core";
import { BLACK_IN_LIGHT_MODE, WHITE_IN_LIGHT_MODE } from "@mapka/style-common";

// Declarative way of defining how colors behave across different variants.

type variantColor = `${string}.${string}`;

interface FactoryParams<T extends string = string> {
  colorName: string; // e.g. white
  color?: string; // e.g. #fff
  borderColor: string;
  lightBg: string;
  filledBg: string;
  hoverColor?: Partial<Record<T, string>>;
  hoverBg?: Partial<Record<T, string>>;
}

interface FactoryBuilderParams<T extends string = string> {
  variants: readonly T[];
  applyColor?: readonly T[];
  applyBorderColor: readonly T[];
  borderless?: readonly T[];
  applyLightBg: readonly T[];
  applyFilledBg: readonly T[];
}

type FactoryProduct = Record<variantColor, VariantColorResolverResult>;

const buildFactory = <T extends string>({
  variants,
  applyColor,
  applyBorderColor,
  borderless,
  applyLightBg,
  applyFilledBg,
}: FactoryBuilderParams<T>) => ({
  colorName,
  color: inputColor,
  borderColor,
  lightBg,
  filledBg,
  hoverColor,
  hoverBg,
}: FactoryParams<T>): FactoryProduct =>
  Object.fromEntries(
    variants.map((variant): [variantColor, VariantColorResolverResult] => {
      const color = applyColor?.includes(variant) && inputColor ? inputColor : BLACK_IN_LIGHT_MODE;
      const background = (
        applyFilledBg.includes(variant) ?
          filledBg :
          applyLightBg.includes(variant) ?
            lightBg :
            "transparent"
      );

      return [
        `${variant}.${colorName}`,
        {
          color,
          background,
          border:
            applyBorderColor.includes(variant) ?
              `var(--mapka-border-base) ${borderColor}` :
              borderless?.includes(variant) ?
                "none" :
                `var(--mapka-border-base) ${BLACK_IN_LIGHT_MODE}`,
          hoverColor: hoverColor?.[variant] ?? color,
          hover: hoverBg?.[variant] ?? background,
        },
      ];
    }),
  );

const BADGES_VARIANTS = ["light", "outline", "dot"] as const;
type BadgeVariant = typeof BADGES_VARIANTS[number];
const badgesFactory = buildFactory<BadgeVariant>({
  variants: BADGES_VARIANTS,
  applyLightBg: ["light"],
  applyFilledBg: ["outline", "dot"],
  applyBorderColor: ["outline", "dot"],
  applyColor: ["outline"],
});

const ACTION_ICON_VARIANTS = ["transparent", "subtle", "default", "filled", "outline", "light"] as const;
type ActionIconVariant = typeof ACTION_ICON_VARIANTS[number];
const actionIconFactory = buildFactory<ActionIconVariant>({
  variants: ACTION_ICON_VARIANTS,
  applyLightBg: ["light"],
  applyFilledBg: ["filled", "outline"],
  applyBorderColor: ["default"],
  borderless: ["transparent", "subtle", "light"],
  applyColor: ["transparent", "subtle", "default", "light"],
});

/**
 * Resolve colors for  Alert, Avatar, Button, Badge and ActionIcon.
 * @see https://mantine.dev/theming/colors/#difference-between-color-and-c-props
 */
export const variantColorResolver: VariantColorsResolver = (input) => {
  const defaultResolvedColors = defaultVariantColorsResolver(input);
  const { colors } = input.theme;

  const redLightBg = `light-dark(${colors.error[1]}, ${alpha(colors.error[8], 0.4)})`;
  const blueLightBg = `light-dark(${colors.info[0]}, ${alpha(colors.info[7], 0.5)})`;
  const greenLightBg = `light-dark(${colors.success[0]}, ${alpha(colors.success[7], 0.5)})`;
  const orangeLightBg = `light-dark(${colors.warning[0]}, ${alpha(colors.warning[7], 0.5)})`;

  const variantColorToResult: Record<variantColor, Partial<VariantColorResolverResult>> = {
    "light.black": {
      color: BLACK_IN_LIGHT_MODE,
      background: `light-dark(${alpha("#868E96", 0.15)}, ${alpha("#ffffff", 0.15)})`,
    },
    "transparent.black": {
      color: BLACK_IN_LIGHT_MODE,
    },
    ...badgesFactory({
      colorName: "purpleBlue",
      color: colors.purpleBlue[8],
      borderColor: colors.purpleBlue[8],
      lightBg: `light-dark(${colors.purpleBlue[1]}, ${colors.purpleBlue[3]})`,
      filledBg: `light-dark(${alpha(colors.purpleBlue[0], 0.5)}, ${alpha(colors.purpleBlue[9], 0.4)})`,
    }),
    ...badgesFactory({
      colorName: "error",
      lightBg: `light-dark(${colors.error[5]}, ${colors.error[6]})`,
      filledBg: `light-dark(${colors.error[0]}, ${alpha(colors.error[8], 0.4)})`,
      borderColor: colors.error[5],
    }),
    ...badgesFactory({
      colorName: "warning",
      lightBg: `light-dark(${colors.orange[7]}, #D59E0A)`, // formerly orange-10
      filledBg: `light-dark(${colors.warning[0]}, ${alpha(colors.warning[7], 0.4)})`,
      borderColor: colors.warning[4],
    }),
    ...badgesFactory({
      colorName: "success",
      color: colors.success[9],
      borderColor: colors.success[9],
      lightBg: `light-dark(${colors.green[5]}, ${colors.green[7]})`,
      filledBg: `light-dark(${colors.success[0]}, ${alpha(colors.success[6], 0.4)})`,
    }),
    ...badgesFactory({
      colorName: "info",
      lightBg: `light-dark(${colors.skyBlue[6]}, #00c2cc)`, // formerly skyBlue-10
      filledBg: `light-dark(${colors.info[0]}, ${alpha(colors.info[7], 0.4)})`,
      borderColor: colors.info[5],
    }),
    ...actionIconFactory({
      colorName: "black",
      color: BLACK_IN_LIGHT_MODE,
      borderColor: BLACK_IN_LIGHT_MODE,
      filledBg: colors.orange[9],
      lightBg: alpha(colors.gray[6], 0.1),
      hoverColor: { filled: WHITE_IN_LIGHT_MODE },
      hoverBg: {
        subtle: alpha(colors.gray[6], 0.1),
        filled: BLACK_IN_LIGHT_MODE,
      },
    }),
    ...actionIconFactory({
      colorName: "red",
      color: colors.error[9],
      borderColor: colors.error[9],
      filledBg: colors.error[5],
      lightBg: redLightBg,
      hoverBg: {
        subtle: redLightBg,
        default: redLightBg,
      },
    }),
    ...actionIconFactory({
      colorName: "blue",
      color: colors.info[9],
      borderColor: colors.info[9],
      filledBg: colors.info[5],
      lightBg: blueLightBg,
      hoverBg: {
        subtle: blueLightBg,
        default: blueLightBg,
      },
    }),
    ...actionIconFactory({
      colorName: "green",
      color: colors.success[9],
      borderColor: colors.success[9],
      filledBg: colors.success[5],
      lightBg: greenLightBg,
      hoverBg: {
        subtle: greenLightBg,
        default: greenLightBg,
      },
    }),
    ...actionIconFactory({
      colorName: "orange",
      color: colors.warning[7],
      borderColor: colors.warning[7],
      filledBg: colors.warning[6],
      lightBg: orangeLightBg,
      hoverBg: {
        subtle: orangeLightBg,
        default: orangeLightBg,
      },
    }),
  };

  return {
    ...defaultResolvedColors,
    ...variantColorToResult[`${input.variant}.${input.color}`],
  };
};
